import cn from 'classnames'
import {
  useGetPartnerData,
  useGetPartnerFiles,
  useGetPartnerFlags,
} from 'legacy/lut/features/partner/hooks'
import { fetchPartnerAttachmentsById } from 'legacy/lut/services/files'
import { fetchPartnerFlagsById } from 'legacy/lut/services/partner/partnerFlags'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { exportPartnerHierarchy, fetchPartnerHierarchy } from 'clickNgo/entities/partner/api'
import { IPartner, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { PartnerEditor } from 'clickNgo/entities/partner/ui/partnerEditor'
import { IAttachment } from 'clickNgo/models/file'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SimpleTable, usePagination } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { CustomTableHeaderCell } from 'shared/components/customTableHeaderCell'
import { sort } from 'shared/components/customTableHeaderCell/helpers'
import { CustomTableColumn, SortCriteria } from 'shared/components/customTableHeaderCell/models'

import { sortPartnerListByHierarchy } from './helpers'
import { partnerHierarchyTranslation } from './translation'

import styles from './PartnerHierarchy.module.scss'

interface Props {
  partnerId: string
  onPartnerSelect: (partner: IPartner) => void
}

const partnerHierarchyColumns: Record<string, CustomTableColumn<IPartner>> = {
  hierarchy: {
    columnTitle: partnerHierarchyTranslation.table.columns.hierarchy,
    columnField: 'hierarchyType',
    styles: { width: '160px' },
  },
  partnerId: {
    columnTitle: partnerHierarchyTranslation.table.columns.partnerId,
    columnField: 'partnerId',
    styles: { width: '160px' },
  },
  partnerName: {
    columnTitle: partnerHierarchyTranslation.table.columns.partnerName,
    columnField: 'communicationName',
    styles: { width: '240px' },
  },
}

export const PartnerHierarchy = memo<Props>(({ partnerId, onPartnerSelect }) => {
  const { status: partnerDataStatus, partnerData: currentPartner } = useGetPartnerData(partnerId)
  const { status: partnerFlagsStatus, ...currentPartnerFlags } = useGetPartnerFlags(partnerId)
  const { status: partnerFilesStatus, partnerFiles: currentPartnerFiles } =
    useGetPartnerFiles(partnerId)
  const currentPartnerId = currentPartner?.partnerId
  const currentTopPartnerId = currentPartner?.topPartnerId
  const currentPartnerRef = useRef(currentPartner)
  currentPartnerRef.current = currentPartner

  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasShadow, setHasShadow] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [partners, setPartners] = useState<IPartner[]>([])
  const [selectedPartner, setSelectedPartner] = useState<IPartner | null>(null)

  const [editablePartner, setEditablePartner] = useState<IPartner | null>(null)
  const [editablePartnerFlags, setEditablePartnerFlags] = useState<IPartnerFlags | null>(null)
  const [editablePartnerAttachments, setEditablePartnerAttachments] = useState<IAttachment[]>([])
  const [sortOrder, setSortOder] = useState<SortCriteria<IPartner>>({})
  const [individualFilters, setIndividualFilters] = useState<
    Partial<Record<keyof IPartner, string>>
  >({})

  const tableRef = useRef<HTMLTableElement>(null)

  const searchIcon = searchQuery ? 'kbase kbase-reload' : 'kbase kbase-search'

  const filteredPartners = useMemo(() => {
    const filtered = partners
      .filter(
        (partner) =>
          partner.communicationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          partner.partnerId.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter((partner) => {
        const filters = Object.entries(individualFilters).filter(([, value]) => Boolean(value))

        if (filters.length) {
          return filters.every(([key, value]) => {
            return partner[key as keyof IPartner]
              ?.toString()
              .toLowerCase()
              .includes(value?.toString().toLowerCase() as unknown as string)
          })
        }

        return true
      })

    return sort(filtered, sortOrder)
  }, [individualFilters, partners, searchQuery, sortOrder])

  const { current: pageSize } = useRef(10)
  const { onPageChange, page, paginatedList, total } = usePagination(filteredPartners, pageSize)

  const handleScroll = () => {
    setHasShadow(Boolean(tableRef?.current?.scrollLeft))
  }

  const onPartnerSelectHandler = useCallback(
    (partner: IPartner) => {
      setSelectedPartner(partner)

      if (onPartnerSelect) {
        onPartnerSelect(partner)
      }
    },
    [onPartnerSelect]
  )

  const onSearchReloadHandler = useCallback(() => {
    setSearchQuery('')
  }, [])

  const onViewPartnerHandler = useCallback(
    async (partner: IPartner, event: React.MouseEvent) => {
      event.stopPropagation()

      if (partner.partnerId === currentPartner?.partnerId) {
        setEditablePartner(currentPartner)
        setEditablePartnerFlags(currentPartnerFlags)
        setEditablePartnerAttachments(currentPartnerFiles)
      } else {
        setIsLoading(true)
        setEditablePartner(partner)
        const partnerFlags = await fetchPartnerFlagsById(partner.partnerId)
        setEditablePartnerFlags(partnerFlags)
        const partnerFiles = await fetchPartnerAttachmentsById(partner.partnerId)
        setEditablePartnerAttachments(partnerFiles)
        setIsLoading(false)
      }
      setIsEditDialogVisible(true)
    },
    [currentPartner, currentPartnerFiles, currentPartnerFlags]
  )

  const onPartnerViewCloseHandler = useCallback(() => {
    setIsEditDialogVisible(false)
    setEditablePartner(null)
    setEditablePartnerFlags(null)
    setEditablePartnerAttachments([])
  }, [])

  const onExportHandler = useCallback(async () => {
    setIsLoading(true)
    if (partnerDataStatus === 'fulfilled' && currentPartner) {
      await exportPartnerHierarchy({
        topPartnerId: currentPartner.topPartnerId,
        searchQuery,
        individualFilters,
      })
    }
    setIsLoading(false)
  }, [currentPartner, individualFilters, partnerDataStatus, searchQuery])

  const onFilterChangeHandler = useCallback((field: keyof IPartner, value: unknown) => {
    setIndividualFilters((prev) => {
      return { ...prev, [field]: value }
    })
  }, [])

  const onSortOrderChangeHandler = useCallback(
    (field: keyof IPartner, value: '' | 'asc' | 'desc') => {
      setSortOder({ [field]: value })
    },
    []
  )

  useEffect(() => {
    let isActual = true

    if (partnerDataStatus === 'fulfilled' && currentTopPartnerId) {
      setIsLoading(true)
      fetchPartnerHierarchy({ partnerId: currentTopPartnerId })
        .then(sortPartnerListByHierarchy)
        .then((partnerList) => {
          if (isActual) {
            setPartners(partnerList)
          }
        })
        .finally(() => {
          if (isActual) {
            setIsLoading(false)
          }
        })
    }

    return () => {
      isActual = false
    }
  }, [currentTopPartnerId, partnerDataStatus])

  useEffect(() => {
    if (
      [partnerDataStatus, partnerFlagsStatus, partnerFilesStatus].includes(RequestStatus.PENDING)
    ) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [partnerDataStatus, partnerFilesStatus, partnerFlagsStatus])

  useEffect(() => {
    setSelectedPartner(currentPartnerRef.current ?? null)
  }, [currentPartnerId])

  return (
    <div className={styles.partnerHierarchy}>
      <LoaderOverlay isVisible={isLoading} color="white" />
      <div className={styles.partnerHierarchyContainer}>
        <div className={styles.partnerHierarchyHeader}>
          <div className={styles.partnerHierarchyTitle}>Partner Hierarchy</div>
          <div className={styles.partnerHierarchyControl}>
            <div className={styles.search}>
              <div className={styles.foundNumber}>
                <Translation
                  text={partnerHierarchyTranslation.controls.found}
                  replaceParams={[{ key: '{length}', value: `${filteredPartners.length}` }]}
                  span
                />
              </div>
              <Input
                classes={{
                  root: styles.searchInput,
                }}
                label={<Translation text={partnerHierarchyTranslation.controls.search} span />}
                value={searchQuery}
                onChange={setSearchQuery}
                endContent={
                  <span
                    onClick={onSearchReloadHandler}
                    className={cn({ [styles.searchBtn]: searchQuery })}
                  >
                    <i className={searchIcon} />
                  </span>
                }
              />
            </div>
            <div className={styles.actions}>
              <Button
                icon="kbase kbase-download"
                color="grey"
                variant="text"
                onClick={onExportHandler}
                loading={isLoading}
              >
                <Translation
                  text={partnerHierarchyTranslation.controls.export}
                  replaceParams={[{ key: '{length}', value: `${filteredPartners.length}` }]}
                  span
                />
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.partnerHierarchyTableWrapper} onScroll={handleScroll} ref={tableRef}>
          <SimpleTable className={cn(styles.table, styles.partnerHierarchyTable)}>
            <SimpleTable.HeaderRow>
              <SimpleTable.HeaderCell
                className={cn(styles.firstColumn, { [styles.shadow]: hasShadow })}
                style={partnerHierarchyColumns.hierarchy.styles}
              >
                <CustomTableHeaderCell
                  column={partnerHierarchyColumns.hierarchy}
                  filter={individualFilters[partnerHierarchyColumns.hierarchy.columnField]!}
                  sortOrder={sortOrder[partnerHierarchyColumns.hierarchy.columnField]}
                  onFilterChange={onFilterChangeHandler}
                  onSortOrderChange={onSortOrderChangeHandler}
                />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell style={partnerHierarchyColumns.partnerId.styles}>
                <CustomTableHeaderCell
                  column={partnerHierarchyColumns.partnerId}
                  filter={individualFilters[partnerHierarchyColumns.partnerId.columnField]!}
                  sortOrder={sortOrder[partnerHierarchyColumns.partnerId.columnField]}
                  onFilterChange={onFilterChangeHandler}
                  onSortOrderChange={onSortOrderChangeHandler}
                />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell style={partnerHierarchyColumns.partnerName.styles}>
                <CustomTableHeaderCell
                  column={partnerHierarchyColumns.partnerName}
                  filter={individualFilters[partnerHierarchyColumns.partnerName.columnField]!}
                  sortOrder={sortOrder[partnerHierarchyColumns.partnerName.columnField]}
                  onFilterChange={onFilterChangeHandler}
                  onSortOrderChange={onSortOrderChangeHandler}
                />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell className={styles.lastColumn} />
            </SimpleTable.HeaderRow>
            {paginatedList.map((partner, index) => (
              <SimpleTable.BodyRow
                className={cn(styles.tBodyRow, {
                  [styles.selectedRow]: selectedPartner?.partnerId === partner.partnerId,
                  [styles.filled]: index % 2,
                })}
                key={partner.partnerId}
                onClick={() => onPartnerSelectHandler(partner)}
              >
                <SimpleTable.BodyCell
                  className={cn(styles.firstColumn, { [styles.shadow]: hasShadow })}
                >
                  <RenderWhen condition={Boolean(partner.entryId406 || partner.entryId502)}>
                    <div className={styles.request} />
                  </RenderWhen>
                  {partner.hierarchyType}
                </SimpleTable.BodyCell>
                <SimpleTable.BodyCell>{partner.partnerId}</SimpleTable.BodyCell>
                <SimpleTable.BodyCell>{partner.communicationName}</SimpleTable.BodyCell>
                <SimpleTable.BodyCell className={styles.lastColumn}>
                  <Button
                    color="white"
                    iconButton="kbase kbase-eye"
                    className={styles.previewBtn}
                    onClick={(event) => onViewPartnerHandler(partner, event)}
                  />
                </SimpleTable.BodyCell>
              </SimpleTable.BodyRow>
            ))}
          </SimpleTable>
        </div>

        <div className={styles.partnerHierarchyFooter}>
          <SimpleTable.Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            classes={{ container: styles.pagination, button: styles.paginationButton }}
            onPageChange={onPageChange}
          />
        </div>
      </div>
      <PartnerEditor
        isVisible={isEditDialogVisible}
        partner={editablePartner!}
        partnerFlags={editablePartnerFlags!}
        partnerAttachments={editablePartnerAttachments}
        onClose={onPartnerViewCloseHandler}
      />
    </div>
  )
})

PartnerHierarchy.displayName = 'PartnerHierarchy'

export const getMetaInfo = () => {
  return {
    urlMapping: [{ propName: 'partnerId', isRequired: true }],
  }
}

export default PartnerHierarchy
